<script lang="ts" generics="R extends XfaiPrograms, T extends XfaiFormComponent<R>">
    import { createUserFocusPowers } from "./modules/UserFocusPowers";    
    import type { Readable } from "svelte/store";
    import { blur, fade, slide } from "svelte/transition";
    import { date } from "svelte-i18n";
    import { LocalDate } from "@/time";
    import { MyFitnessStores } from "@/MyFitness/stores";
    import { durationToHoursMinutes, getCurrentFitnessSignature, getCurrentTrainingLoads, getDisplayWeight, round, scrollToElement, uniqueId, type XSSDetails } from "@/Util/utils";
    import { ProgramAchievabilityStatusIcon, CurrentForecastedTargetSquare, ForecastResultsDescription, XfaiXss, ProgramNameIcon, FocusPowerIncrease } from "./ProgramElements";
    import type { AchievabilityStatus, ProgramAchievability } from "./modules/ProgramAchievability";
    import { XfaiBall, StickyButtonBannerHeader, InfoIcon  } from "@/Common";
    import { getContext, onMount, type ComponentProps, tick } from "svelte"
    import { ProgramType, programTypeDescription, programTypeTitle, type XfaiFormComponent, type ForecastedTargets, emptyForecastedTargets, type ForecastInfo, defaultForecastInfo, shortProgramTypeTitle, type XfaiPrograms, SCHEDULE_WORKOUTS_HELP } from "@/Common/constants";
    import TrainingForecaster from "../MyFitness/TrainingForecast/TrainingForecaster.svelte";
    import {Modal} from '@/Bootstrap';
    import Label from "@/lib/components/ui/label/label.svelte";
    import * as RadioGroup from "@/lib/components/ui/radio-group";
    import * as Sheet from "$lib/components/ui/sheet/index.js";
    import { Separator } from "@/lib/components/ui/separator";
    import RaceDate from "@/ExerciseCard/RaceDate.svelte";
    import EventReadiness from "./ProgramElements/EventReadiness.svelte";
    import { Checkbox } from "@/lib/components/ui/checkbox";
    import HelpTooltip from "@/Common/HelpTooltip.svelte";
    import { addMonths, startOfDay } from "date-fns";

    // Grab context
    const myFitness = getContext<MyFitnessStores>('myFitness')
    const { trainingPlan, userParams, trainingAdvice } = myFitness
    let currentSignature: XSSDetails
    let currentTL: XSSDetails
    $: if(trainingAdvice) {
        currentSignature = getCurrentFitnessSignature($trainingAdvice)
        currentTL = getCurrentTrainingLoads($trainingAdvice)
        focusPower.updateFocusPower($plan.targetAthlete)
    }

    /* Current Focus Power and Duration */
    let focusPower = createUserFocusPowers(trainingAdvice)
    const {currentFocusPower, focusPowerDuration} = focusPower
    $: focusPower.updateFocusPower($plan.targetAthlete)

    export let formComponent: T
    export let plan: ComponentProps<InstanceType<T>>["plan"]
    export let forecastResults: any
    export let cancelForecast = () => {}
    export let programType: R
    export let savingResults = false
    export let onSave = () => {}
    export let adapt = false
    /* First time setup (account wizard): only the form, run from its own button, with simplified results */
    export let firstTime = false
    export let onSkip = () => {}
    /* With it, a Previous button joins Generate and Skip on the bottom row (the wizard's step before) */
    export let onPrevious: (() => void) | null = null
    export let formProps: Record<string, any> = {} // extra props for the form component
    /* Assign workouts to the forecasted days when the plan is saved. Defaults to on for plans ending within 3 months. */
    export let scheduleWorkouts = true
    let scheduleWorkoutsTouched = false
    $: if (!scheduleWorkoutsTouched) scheduleWorkouts = startOfDay($plan.targetDate) <= startOfDay(addMonths(new Date(), 3))

    /* Forecaster */
    let forecastInfo: ForecastInfo = {...defaultForecastInfo}
    let forecastInputs = null
    let forecastedTargets: ForecastedTargets
    let adaptChoice = 'adapt'
    let trainingForecast: () => Promise<void>
    
    /* Form */
    let refreshForm: () => void
    let forecastReady: boolean
    let achievabilityId: string
    let programAchievability: ProgramAchievability
    let overallAchievability: Readable<AchievabilityStatus>
    
    /* Dialogs */
    let showModal: () => void
    let closeModal: () => void
    let sheetOpen = false

    export async function runForecast() {
        sheetOpen = true
        await tick()
        forecastedTargets = {...emptyForecastedTargets}
        trainingForecast()
        // scrollToElement("training-forecaster", -20)   
    }
    
    // Adapting re-optimizes the existing plan's days from today, so it can't move the plan's start date.
    // A different start date than the existing plan's always creates a new plan.
    $: existingStartKey = $trainingPlan?.is_pre_plan && $trainingPlan.planned_start_date ? LocalDate.format(new Date($trainingPlan.planned_start_date)) : null
    $: chosenStartKey = $plan?.startDate ? LocalDate.format($plan.startDate) : null
    $: startDateChanged = existingStartKey !== chosenStartKey
    $: if (startDateChanged && adaptChoice === 'adapt') adaptChoice = 'no'

    $: adapt = $trainingPlan?.hasForecast && adaptChoice == 'adapt' && !startDateChanged

    $: if(!forecastInputs) {
        cancelForecast()
    }

    $: if (forecastInfo.done) {

        if(forecastInfo.results) {
            forecastResults = forecastInfo.results
            const final = forecastResults['days']?.find(d => d.state.includes('event_A'));
            forecastedTargets = {
                signature: {
                    xss: null,
                    xlss: final.ftp,
                    xhss: final.hie,
                    xpss: final.pp,
                },
                trainingLoads: {
                    xss: final.tls.l + final.tls.h + final.tls.p,
                    xlss: final.tls.l,
                    xhss: final.tls.h,
                    xpss: final.tls.p,
                },
                focusPower: final.fp,
                xss: {
                    xss: final.xss,
                    xlss: final.xlss,
                    xhss: final.xhss,
                    xpss: final.xpss,
                }
            }
        }
    }

    onMount(() => {
        ({overallAchievability} = programAchievability);
    })

    async function closeForecaster() {
        cancelForecast()
        if (!savingResults)
            forecastResults = null
        forecastedTargets = {...emptyForecastedTargets}
        forecastInfo = {...defaultForecastInfo}
    }

    // $: console.log(forecastInfo)
</script>

{#if firstTime}
    <svelte:component this={formComponent} class="relative flex-1" {...formProps} {plan} bind:forecastInputs bind:programAchievability disabled={forecastInfo.running || sheetOpen} bind:achievabilityId bind:forecastReady bind:refresh={refreshForm}>
        <svelte:fragment slot="generate">
            <!-- One row: Previous at the start, Skip and Generate at the end. On phones Generate gets the full width, with
                 Previous and Skip below it. -->
            <div class="grid grid-cols-2 sm:grid-cols-[auto,1fr,auto,auto] items-center gap-3 sm:gap-4 w-full mt-2">
                <button class="btn-xfai group/crysball border-none text-white bg-[--color-xert-blue] hover:bg-[--color-xert-teal] text-base px-6 py-3 justify-center col-span-2 row-start-1 sm:col-span-1 sm:col-start-4"
                    id="forecast-btn" disabled={!forecastInputs || !forecastReady} type="button"
                    on:click={runForecast}
                >
                    <XfaiBall class="h-6 mr-2" type="hover"/>
                    Generate a Forecast AI Training Plan
                </button>
                {#if onPrevious}
                    <button class="btn-xfai border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 justify-self-start row-start-2 sm:row-start-1 sm:col-start-1"
                        type="button" disabled={forecastInfo.running || savingResults} on:click={onPrevious}>Previous</button>
                {/if}
                <button class="text-sm text-slate-500 hover:text-slate-800 underline underline-offset-2 justify-self-end row-start-2 sm:row-start-1 sm:col-start-3"
                    type="button" on:click={onSkip}>Skip for now</button>
            </div>
        </svelte:fragment>
    </svelte:component>
{:else}
<StickyButtonBannerHeader class="max-w-[1800px] w-full">

    <div class="flex justify-center items-center gap-3 px-4 pb-4 pt-3 md:px-8 md:pb-8 md:pt-6" slot="header">
        <XfaiBall class="w-24 h-24 text-xerteal-50" type="animated"/>
        <div class="flex flex-col">
            <span class="font-black text-2xl md:text-4xl text-white"> {programTypeTitle[programType]} </span>
            <span class="text-base text-xerteal-100"> {programTypeDescription[programType]} </span>
        </div>   
    </div> 

    <svelte:fragment slot="sticky">
        {#if forecastReady && !forecastInfo.running && !forecastResults}
            <div in:slide|local={{delay: 200}} out:slide|local={{duration: 200}}>
                <div class="flex flex-row gap-2 bg-white rounded-b p-2 md:px-5 md:py-4 shadow-md shadow-black/10 justify-end border-t-4 border-xerteal-400/10 border-solid border-b-0 border-x-0">
                    <button class="btn-xfai border-none bg-transparent hover:bg-xerteal-50 text-xerteal-700 gap-3 text-sm" on:click={() => scrollToElement(achievabilityId, -30)}>
                        Achievability <i class="fa-question fa-regular text-xs -ml-2"/> <ProgramAchievabilityStatusIcon status={$overallAchievability} showCheck/>
                    </button>

                    <button class="w-max-content btn-xfai group/crysball border-none text-white bg-[--color-xert-blue] hover:bg-[--color-xert-teal]" 
                        id="forecast-btn" data-toggle="tooltip" disabled={!forecastInputs} type="button"
                        on:click={() => {
                            if (($overallAchievability === "error")) {
                                showModal()
                            } else {
                                runForecast()
                            }
                        }}
                    >
                        <XfaiBall class="h-5 mr-1" type="hover"/>
                        Run
                        <span class="hidden whitespace-pre md:block">{' Forecast AI'}</span>
                    </button>

                </div>
            </div>
        {/if}
    </svelte:fragment>

    <div class="flex flex-col gap-4" slot="body">
        <svelte:component this={formComponent} class="relative bg-white shadow-md shadow-black/10 px-5 pt-5 md:px-8 md:pt-8 pb-4 rounded flex-1" {plan} bind:forecastInputs bind:programAchievability disabled={forecastInfo.running || sheetOpen} bind:achievabilityId bind:forecastReady bind:refresh={refreshForm} hideStartDate={adapt}>
            <svelte:fragment slot="adapt-choice">
                {#if $trainingPlan && $trainingPlan.hasForecast && $trainingPlan.start_date}
                    {@const adaptOptionId = uniqueId('xfaiAdapt')}
                    {@const noAdaptOptionId = uniqueId('xfaiNoAdapt')}
                    <div class="flex flex-col gap-3 col-span-2 border px-4 py-2 shadow-sm rounded text-left twp">
                        <span class="text-muted-foreground text-sm">Training Plan</span>
                        <RadioGroup.Root class="grid grid-cols-[min-content,auto] gap-x-3 items-center" 
                            bind:value={adaptChoice} 
                            onValueChange={refreshForm}
                        >         
                            <RadioGroup.Item value="no" id={noAdaptOptionId} />
                            <Label class="font-normal text-sm mb-0" for={noAdaptOptionId}><span class="font-medium">Create </span>new training plan</Label>
                            <RadioGroup.Item value="adapt" id={adaptOptionId} disabled={startDateChanged} />
                            <Label class="font-normal text-sm mb-0" for={adaptOptionId}><span class="font-medium">Update </span>existing training plan from {$date(new Date($trainingPlan.start_date), {dateStyle: 'long'})}{#if startDateChanged} <span class="text-muted-foreground">(not available when the start date changes)</span>{/if}</Label>
                        </RadioGroup.Root>
                    </div>
                {/if}
            </svelte:fragment>
        </svelte:component>
    </div>
</StickyButtonBannerHeader>
{/if}



<!-- TODO: Change this to popover -->
<Modal bind:show={showModal} bind:close={closeModal} header={false}>
    <div class="grid grid-cols-[min-content,auto] gap-x-7 items-center gap-y-1">
        <i class="fa-light fa-circle-xmark text-5xl text-red-600 row-span-2"/>
        <span class="col-start-2 text-lg font-bold">{"Run Forecast ?"}</span>
        <span class="text-slate-600 text-base">You will likely not be able to reach the estimated targets resulting in a different training plan and final targets.</span>   
    </div>

    <svelte:fragment slot="footer">
        <div class="flex gap-2 justify-end items-baseline">
            <button class="btn-xfai border border-slate-600 text-slate-600 bg-white hover:bg-[--color-xert-blue] hover:text-white hover:border-[--color-xert-blue]" 
                on:click={closeModal}
            >
                Cancel
            </button>
            <button class="btn-xfai border-none text-white bg-[--color-xert-teal] hover:bg-[--color-xert-blue]" 
                on:click={() => {
                    closeModal()
                    runForecast()
                }}
            >
                Run
            </button>
        </div>
    </svelte:fragment> 
</Modal>

<Sheet.Root bind:open={sheetOpen} onOpenChange={closeForecaster} portal="#shadcn-ui-dialog-target" closeOnEscape={false} closeOnOutsideClick={false} openFocus="#results-sheet">
    <Sheet.Content id="results-sheet" class="max-h-[96%] overflow-auto focus:outline-none" side="bottom">
        <div class="flex flex-col p-3 max-w-screen-2xl mx-auto">

            {#if forecastInfo.done}
            <div in:slide={{delay:1200, duration:900}}>
                <div class="grid xl:grid-cols-[1fr,min-content,3fr] border shadow p-3 lg:py-4 lg:px-6 rounded">

                    <div class="flex flex-col gap-4 xl:gap-8 self-center">
                        <div in:blur={{delay:2100, duration:900}}>
                            <ForecastResultsDescription {forecastInfo} overallAchievability={$overallAchievability} focusPowerDuration={$focusPowerDuration}/>
                        </div>
                        <div class="flex flex-col gap-3 w-full" in:blur={{delay:5000, duration:900}}>
                            <label class="twp flex items-start gap-3 rounded-md border bg-slate-50 px-3 py-2.5 cursor-pointer" for="schedule-workouts">
                                <Checkbox id="schedule-workouts" class="mt-0.5" bind:checked={scheduleWorkouts} on:click={() => scheduleWorkoutsTouched = true} disabled={savingResults}/>
                                <span class="flex flex-col leading-snug">
                                    <span class="flex items-center gap-1">
                                        <span class="text-sm font-medium">Schedule Workouts</span>
                                        <HelpTooltip message={SCHEDULE_WORKOUTS_HELP} label="About Schedule Workouts" iconClass="w-5 h-5 text-gray-500"/>
                                    </span>
                                    <span class="text-xs text-slate-500">
                                        {scheduleWorkouts ? "A suitable workout is assigned to each training day." : "Training days are saved as plain activities. You can choose workouts later."}
                                    </span>
                                </span>
                            </label>
                            <div class="flex flex-wrap-reverse gap-2 justify-end">
                                <Sheet.Close asChild let:builder>
                                    <button 
                                        class="btn-xfai border hover:bg-accent hover:text-accent-foreground" 
                                        type="button" data-toggle="tooltip"
                                        use:builder.action {...builder}
                                    >   
                                        Cancel
                                    </button>
                                </Sheet.Close>
                                <Sheet.Close asChild let:builder>
                                    <button 
                                        class="btn-xfai border-none text-white bg-xerteal-400 hover:bg-[--color-xert-lime]" 
                                        type="button" data-toggle="tooltip" disabled={forecastResults === null || forecastInfo.running || savingResults}
                                        on:click={()=>{ savingResults = true; onSave(); }}  
                                        use:builder.action {...builder}
                                    >   
                                        Save Training Plan
                                    </button>
                                </Sheet.Close>
                            </div>
                        </div>
                    </div>

                    <div in:fade={{delay:2500}}>
                        <Separator orientation="vertical" class="hidden xl:block mx-6" />
                        <Separator orientation="horizontal" class="xl:hidden mt-2 mb-6" />
                    </div>
                    
                    {#if forecastResults}
                        <div class="flex flex-wrap">

                            <div class="flex flex-col gap-6">

                                <div class="flex flex-wrap gap-y-3 gap-x-6 items-start">
                                    {#if !firstTime}
                                    <div in:blur|global={{delay:3000, duration:900}}>
                                        {#if $plan.program === ProgramType.Race && $plan.raceLogoUrl}
                                            <img class="h-24 sm:h-32 aspect-[800/360] object-cover rounded-md" src={$plan.raceLogoUrl} alt='race-logo' />
                                        {:else}
                                            <div class="flex items-center border border-xerteal-100 rounded-md h-24 sm:h-32 aspect-[800/360] px-4 py-2">
                                                <ProgramNameIcon class="m-auto w-max" {programType} />
                                            </div>               
                                        {/if}
                                    </div>
                                    {/if}
                                    <div class="flex flex-col gap-y-5" in:slide|global={{delay:3500, duration:900}}>
                                        <span class="font-semibold text-lg leading-none tracking-tight">
                                            {#if $plan.program === ProgramType.Race}
                                                {$plan.name}
                                            {/if}
                                            {#if $plan.program === ProgramType.Event}
                                                {$plan.name} - {$plan.terrain} {$plan.type}
                                            {/if}
                                        </span>
                                        <div class="flex gap-y-5 gap-x-8 flex-wrap text-base">
                                            {#if $plan.program === ProgramType.Goal}
                                                <FocusPowerIncrease
                                                    class="flex-nowrap flex-col"
                                                    currentFocusPower={$currentFocusPower} projectedFocusPower={forecastedTargets.focusPower}
                                                    date={$plan.targetDate} focusPowerDuration={$focusPowerDuration}
                                                />
                                            {/if}

                                            {#if $plan.program !== ProgramType.Goal}
                                                <div class="flex flex-col gap-1 leading-none">
                                                    <RaceDate class="w-20" dateClass="text-3xl" monthClass="h-7 text-sm" date={$plan.targetDate}/>
                                                </div>
                                                <div class="flex flex-col gap-1 leading-none order-5">
                                                    <span class="text-slate-500 tracking-tight text-sm leading-none">Event Readiness</span>
                                                    <EventReadiness taper={$plan.settings.taper}/>
                                                </div>
                                            {/if} 

                                            {#if !firstTime}
                                            <div class="flex flex-col gap-1 leading-none">
                                                <span class="text-slate-500 tracking-tight text-sm leading-none">
                                                    Focus & Specificity
                                                </span>
                                                <span class="">{$plan.specificity} {$plan.targetAthlete}</span>
                                            </div>
                                            {/if}

                                            {#if $plan.program === ProgramType.Event}
                                                <div class="flex flex-col gap-1 leading-none">
                                                    <span class="text-slate-500 tracking-tight text-sm leading-none">
                                                        Duration
                                                    </span>
                                                    <span class="">{durationToHoursMinutes($plan.duration)}</span>
                                                </div>
                                            {/if}

                                            {#if $plan.program === ProgramType.Race}
                                                <div class="flex flex-col gap-1 leading-none">
                                                    <span class="text-slate-500 tracking-tight text-sm leading-none">
                                                        Target Weight
                                                    </span>
                                                    <span class="">{$plan.userTargetWeight} {$userParams.weight_unit}</span>
                                                </div>
                                            {/if}   
                                        </div>
                                    </div>                            

                                </div>
                            
                                <div class="flex flex-wrap gap-y-4 gap-x-8">
                                    
                                    <div class="flex flex-col gap-1" in:slide|global={{delay:4000, duration:900}}>
                                        <span class="text-slate-500 tracking-tight text-sm leading-none">Final Signature & Focus</span>
                                        <div class="flex flex-wrap">
                                            <CurrentForecastedTargetSquare current={round(currentSignature.xlss)} final={round(forecastedTargets.signature.xlss)} unit="w" finalClass="text-[--color-xlss]" title="TP"/>
                                            <CurrentForecastedTargetSquare current={round(currentSignature.xhss/1000, 1)} final={round(forecastedTargets.signature.xhss/1000, 1)} unit="kJ" finalClass="text-[--color-xhss]" title="HIE"/>
                                            <CurrentForecastedTargetSquare current={round(currentSignature.xpss)} final={round(forecastedTargets.signature.xpss)} unit="w" finalClass="text-[--color-xpss]" title="PP"/>
                                            <CurrentForecastedTargetSquare current={round($currentFocusPower)} final={round(forecastedTargets.focusPower)} unit="w" finalClass="text-xerteal-600" title="Focus"/>
                                        </div>
                                    </div>
                                    {#if !firstTime}
                                    <div class="flex flex-col gap-1" in:slide|global={{delay:4500, duration:900}}>
                                        <span class="text-slate-500 tracking-tight text-sm leading-none">Final Training Loads</span>
                                        <div class="flex flex-wrap">
                                            <CurrentForecastedTargetSquare current={round(currentTL.xlss)} final={round(forecastedTargets.trainingLoads.xlss)} finalClass="text-[--color-xlss]" title="Low"/>
                                            <CurrentForecastedTargetSquare current={round(currentTL.xhss, 1)} final={round(forecastedTargets.trainingLoads.xhss, 1)} finalClass="text-[--color-xhss]" title="High"/>
                                            <CurrentForecastedTargetSquare current={round(currentTL.xpss, 1)} final={round(forecastedTargets.trainingLoads.xpss, 1)} finalClass="text-[--color-xpss]" title="Peak"/>
                                        </div>
                                    </div>
                                    {/if}

                                    {#if $plan.program !== ProgramType.Goal}
                                    <div class="flex flex-col gap-1" in:slide|global={{delay:5000, duration:900}}>
                                        <span class="text-slate-500 tracking-tight text-sm leading-none">{shortProgramTypeTitle[$plan.program]} XSS</span>
                                        <XfaiXss class="border rounded px-3 py-2" xss={forecastedTargets.xss} />
                                    </div>
                                    {/if}
                                    
                                </div>
                            </div>

                        </div>
                    {/if}
                </div>
            </div>
            {/if}

            <Separator class="mb-2 mt-4"/>

            <TrainingForecaster
                hidden={!forecastInfo.running && !forecastInfo.results}
                id="training-forecaster"
                cal_settings={$plan.cal_settings} {forecastInputs} {adapt}
                bind:cancelForecast bind:trainingForecast
                bind:forecastInfo
            />
            
        </div>
    </Sheet.Content>
</Sheet.Root>
